import { useApolloClient } from '@apollo/client';
import { Table, TextAlign } from '@busybusy/webapp-react-ui';
import { Equipment, EquipmentCategory, EquipmentCategoryUpdateInput } from '__generated__/graphql';
import { SIMPLE_EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import classNames from 'classnames';
import MenuButton from 'components/foundation/buttons/MenuButton/MenuButton';
import EmptyState from 'components/foundation/state-templates/EmptyState/EmptyState';
import ManageListTableDialog from 'containers/jsa-reports/ManageListTableDialog/ManageListTableDialog';
import useEquipmentCategories from 'containers/manage-euipment/hooks/useEquipmentCategories';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useOpenable } from 'hooks';
import { t } from 'i18next';
import { isNil, isNull } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import CreateEquipmentCategoryDialog from '../CreateEquipmentCategoryDialog/CreateEquipmentCategoryDialog';
import EquipmentCatMakeModelDeleteProhibitedDialog from '../EquipmentCatMakeModelDeleteProhibitedDialog/EquipmentCatMakeModelDeleteProhibitedDialog';
import './ManageEquipmentCategoriesDialog.scss';

export interface IManageEquipmentCategoriesDialogProps {
  className?: ClassName;
  isOpen: boolean;
  onClose: () => void;
}

const ManageEquipmentCategoriesDialog = (props: IManageEquipmentCategoriesDialogProps) => {
  const { className, isOpen, onClose } = props;
  const createEditDialog = useOpenable();
  const deleteDialog = useOpenable();
  const deleteProhibitedDialog = useOpenable();

  const client = useApolloClient();

  const { getEquipmentCategories, deleteEquipmentCategory } = useEquipmentCategories();

  const [data, setData] = useState<EquipmentCategory[]>([]);
  const [selectedRow, setSelectedRow] = useState<EquipmentCategory | null>(null);
  const [canDelete, setCanDelete] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const getData = async () => {
    const categories: EquipmentCategory[] = await getEquipmentCategories();
    setData(categories);
  };

  const hasConnectedEquipment = async (cat?: EquipmentCategory): Promise<boolean> => {
    const category = !isNull(selectedRow) ? selectedRow : cat;

    if (!isNil(category)) {
      const { data } = await client.query<{ equipment: Equipment[] }>({
        query: SIMPLE_EQUIPMENT_QUERY,
        variables: {
          first: 1,
          filter: { model: { equipmentCategoryId: { equal: category?.id } }, deletedOn: { isNull: true } },
        },
        fetchPolicy: 'network-only',
      });

      return data.equipment.length > 0;
    }
    return true;
  };

  const handleSubmit = () => {
    createEditDialog.close();
    getData();
  };

  const handleDelete = async () => {
    createEditDialog.close();
    if (!isNil(selectedRow)) {
      const deleteInput: EquipmentCategoryUpdateInput = {
        id: selectedRow.id,
        deletedOn: DateTime.now().toISO(),
      };

      try {
        await deleteEquipmentCategory(deleteInput);
        deleteDialog.close();
        getData();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete equipment category:', error);
      }
      setSelectedRow(null);
      setCanDelete(false);
    }
  };

  const onDeleteClicked = async (row: EquipmentCategory) => {
    const deleteProhibited = await hasConnectedEquipment(row);
    setSelectedRow(row);
    if (deleteProhibited) {
      deleteProhibitedDialog.open();
    } else {
      deleteDialog.open();
    }
  };

  const getColumns = () => {
    return [
      {
        key: 'actions',
        sort: false,
        cell: (row: EquipmentCategory) => {
          return <MenuButton options={[{ label: t('Delete'), onSelect: () => onDeleteClicked(row) }]} />;
        },
        size: '44px',
        cellClassName: 'px-0',
        align: TextAlign.CENTER,
      },
      {
        key: 'title',
        title: 'Category',
        sort: false,
        cell: (row: EquipmentCategory) => {
          const category: EquipmentCategory = row;
          return <div className="project-title-container">{category.title}</div>;
        },
      },
    ];
  };

  const handleRowClick = async (row: EquipmentCategory) => {
    setSelectedRow(row);
    const result = await hasConnectedEquipment(row);
    setCanDelete(!result);
    createEditDialog.open();
  };

  const handleCreateEditDialogClose = () => {
    setSelectedRow(null);
    setCanDelete(false);
    createEditDialog.close();
  };

  const handleDeleteProhibitedClose = () => {
    setSelectedRow(null);
    deleteProhibitedDialog.close();
  };

  const classes = classNames('manage-equipment-categories-dialog', className);

  return (
    <>
      <ManageListTableDialog
        title={t('Categories')}
        onAdd={createEditDialog.open}
        isOpen={isOpen}
        onClose={onClose}
        addText={t('Create')}
        className={classes}
      >
        <Table
          strokeCols={false}
          className={'category-table mb-5'}
          data={data}
          cols={getColumns()}
          onRowClick={handleRowClick}
          emptyTemplate={
            <EmptyState
              title={t('No Categories')}
              subtitle={t('Press the Create button above to begin adding categories.')}
            />
          }
        />
      </ManageListTableDialog>
      <CreateEquipmentCategoryDialog
        isOpen={createEditDialog.isOpen}
        onClose={handleCreateEditDialogClose}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        canDelete={canDelete}
        selectedRow={selectedRow}
      />
      <EquipmentCatMakeModelDeleteProhibitedDialog
        isOpen={deleteProhibitedDialog.isOpen}
        onClose={handleDeleteProhibitedClose}
        type={'category'}
      />
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title={t('Delete category?')}
        onClose={deleteDialog.close}
        onDelete={handleDelete}
        showHeaderDivider={false}
      >
        {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
      </DeleteConfirmationDialog>
    </>
  );
};

export default ManageEquipmentCategoriesDialog;
